// @flow
import React, { forwardRef, useContext } from 'react';
import classnames from 'classnames';

import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { OVER_BOT } from 'Components/frameworks/portal/Portal.constants';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';
import Portal from 'Components/frameworks/portal/Portal.jsx';

type Props = {
  color?: Colors,
  icon?: IconName,
  label: string,
  onClose: () => mixed,
  portalName?: string,
  visible?: boolean,
};

export const Toast = forwardRef<Props, *>((props: Props, ref) => {
  const { isAppMounted } = useContext(ResponsiveContext);

  if (!isAppMounted) {
    return null;
  }

  const {
    color = COLORS.WATER_GREEN,
    icon = ICONS.CONFIRMATION,
    label = '',
    onClose,
    portalName = OVER_BOT,
    visible,
  } = props;

  if (!visible) {
    return null;
  }

  return (
    <Portal name={portalName}>
      <button
        aria-disabled={!visible}
        aria-hidden={!visible}
        className={classnames(
          'reset-button bg-white fixed flex font-bold items-center p-1 rtl:right-0 rounded-small transition-all',
          {
            [`text-${String(color)}`]: color,
            'rtl:translate-x-100': !visible,
            'rtl:mr-10 shadow-md': visible,
          },
        )}
        data-testid="Toast"
        onClick={onClose}
        ref={ref}
        style={{
          top: 150,
          width: 280,
        }}
        tabIndex={visible ? 0 : -1}
        type="button"
      >
        <Icon className="flex-shrink-0 m-1" name={icon} width="30px" />
        <Text className="flex-1 p-1 rtl:mr-6" text={t(label)} />
        <div className="absolute leading-none p-3 top-0 rtl:right-0">
          <Icon name={ICONS.CROSS} width="10px" />
        </div>
      </button>
    </Portal>
  );
});
